import { DatePicker, Spin } from 'antd';
import vi from 'antd/es/date-picker/locale/vi_VN';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import * as dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import statsApi from '../../apis/stats';
import userApi from '../../apis/user';
import { TrackingFilter } from '../../constants/order';
import { useHasMounted } from '../../hooks';
import {
  ProductTemplateFulfillmentStatsResponse,
  StatsResponse,
} from '../../interfaces/Stats';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { PageContainer } from '../../styles/Global';
import { SpinContainer } from '../Order/Order.styled';
import PageHeader from '../PageHeader/PageHeader';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import ProductTemplatesTable from '../ProductTemplateStats/ProductTemplatesTable';
import {
  CardStats,
  CardStatsContainer,
  ChartContainer,
  ChartWrapper,
  Row,
  SearchFilterContainer,
  StyledSelect,
} from './Statistics.styled';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
);

const Statistics = () => {
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const isAdmin = currentUser?.role === Role.ADMIN;
  const [trackingFilter] = useState<TrackingFilter>();
  const [filterDate, setFilterDate] = useState<{
    startDate: any;
    endDate: any;
  }>();
  const [statsData, setStatsData] = useState<StatsResponse>({
    totalShippedOrders: 0,
    totalRevenue: 0,
    totalCost: 0,
    totalOrders: 0,
    totalCancelledOrders: 0,
    totalReshippedOrders: 0,
    totalReshippedOrdersCost: 0,
    totalRefundedOrders: 0,
    totalRevenueByMonthsDataset: { data: [], labels: [] },
    totalCostByStatusDataset: { data: [], labels: [] },
    totalShippedOrdersByMonthsDataset: { data: [], labels: [] },
    totalOrdersByStatusDataset: { data: [], labels: [] },
  });
  const [
    productTemplateFulfillmentStatsData,
    setProductTemplateFulfillmentStatsData,
  ] = useState<ProductTemplateFulfillmentStatsResponse>({
    totalProductTemplatesFulfilled: 0,
    totalQuantityFulfilled: 0,
    totalRevenue: 0,
    productTemplates: [],
  });
  const [loading, setLoading] = useState(false);
  const [userOption, setUserOption] = useState<
    { label: string; value: string }[]
  >([]);
  const [username, setUsername] = useState('');
  const mounted = useHasMounted();

  useEffect(() => {
    if (!mounted() || !isAdmin) {
      return;
    }

    fetchUser();
  }, [isAdmin, mounted]);

  const fetchUser = async () => {
    try {
      const res = await userApi.listUsers({ page: 1, perPage: 50, all: true });
      const tmp = res.items.map((user) => {
        return { label: user.name, value: user.username };
      });
      tmp.unshift({ label: 'View All', value: '' });
      setUserOption(tmp);
    } catch (error) {
      setUserOption([]);
      console.error(error);
    } finally {
    }
  };

  const handleStats = async () => {
    try {
      if (!isAdmin && !currentUser?.username) return;
      setLoading(true);
      let query = {
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
        tracking: trackingFilter,
      };
      const res = !isAdmin
        ? await statsApi.stats(query)
        : !!username
        ? await statsApi.userStats(username, query)
        : await statsApi.stats(query);
      setStatsData(res);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductTemplateFulfillmentStats = async () => {
    try {
      if (!isAdmin && !currentUser?.username) return;
      setLoading(true);
      let query = {
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
      };
      const res = !isAdmin
        ? await statsApi.productTemplateFulfillmentStats(query)
        : !!username
        ? await statsApi.productTemplateFulfillmentStatsByUser(username, query)
        : await statsApi.productTemplateFulfillmentStats(query);
      setProductTemplateFulfillmentStatsData(res);
      setLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <PageHeader title="Thống kê" />
      <SearchFilterContainer $isAdmin={isAdmin}>
        <DatePicker.RangePicker
          onChange={(val) => {
            if (Array.isArray(val) && val.length > 1) {
              setFilterDate({
                startDate: dayjs.default(val[0]).toISOString(),
                endDate: dayjs.default(val[1]).toISOString(),
              });
            } else {
              setFilterDate({
                startDate: null,
                endDate: null,
              });
            }
          }}
          allowClear
          locale={vi}
        />
        {isAdmin && (
          <StyledSelect
            className="w-100"
            placeholder="Chọn người dùng"
            onChange={(val: any) => {
              setUsername(val);
            }}
            options={userOption}
          />
        )}
        <PrimaryButton
          className="font-weight--500"
          key="1"
          onClick={() => {
            handleStats();
            handleProductTemplateFulfillmentStats();
          }}
        >
          Áp dụng
        </PrimaryButton>
      </SearchFilterContainer>
      <div>
        <CardStatsContainer>
          <CardStats title="Tổng đơn hàng" value={statsData.totalOrders} />
          <CardStats
            title="Tổng đơn hàng bị huỷ"
            value={statsData.totalCancelledOrders}
          />
          <CardStats
            title="Tổng đơn hàng được hoàn"
            value={statsData.totalRefundedOrders}
          />
          <CardStats
            title="Tổng chi phí"
            value={statsData.totalCost}
            formatter={(value) => `${value?.toLocaleString('vi-VN')} VNĐ`}
          />
        </CardStatsContainer>

        <Row>
          <ChartWrapper>
            <p>Thống kê đơn hàng theo trạng thái</p>
            <ChartContainer>
              <Pie
                height="200px"
                width="200px"
                options={{ maintainAspectRatio: false }}
                data={{
                  labels: statsData.totalOrdersByStatusDataset.labels,
                  datasets: [
                    {
                      label: '# Tổng',
                      data: statsData.totalOrdersByStatusDataset.data || [],
                      backgroundColor: [
                        'rgba(255, 99, 132, 0.2)',
                        'rgba(54, 162, 235, 0.2)',
                        'rgba(255, 206, 86, 0.2)',
                      ],
                      borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </ChartContainer>
          </ChartWrapper>
          <ChartWrapper>
            <p>Thống kê chi phí theo trạng thái</p>
            <ChartContainer>
              <Pie
                height="200px"
                width="200px"
                options={{ maintainAspectRatio: false }}
                data={{
                  labels: statsData.totalCostByStatusDataset.labels,
                  datasets: [
                    {
                      label: '# Tổng',
                      data: statsData.totalCostByStatusDataset.data || [],
                      backgroundColor: [
                        'rgba(255, 99, 132, 0.2)',
                        'rgba(54, 162, 235, 0.2)',
                        'rgba(255, 206, 86, 0.2)',
                        'rgba(75, 192, 192, 0.2)',
                        'rgba(153, 102, 255, 0.2)',
                        'rgba(255, 159, 64, 0.2)',
                      ],
                      borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(153, 102, 255, 1)',
                        'rgba(255, 159, 64, 1)',
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </ChartContainer>
          </ChartWrapper>
        </Row>

        {/* Product Template Fulfillment Statistics */}
        <div style={{ marginTop: '40px' }}>
          <h2
            style={{
              marginBottom: '20px',
              fontSize: '20px',
              fontWeight: '600',
            }}
          >
            Thống kê mẫu sản phẩm
          </h2>
          {/* 
          <ProductTemplateStatsCards
            data={productTemplateFulfillmentStatsData}
          /> */}

          <ProductTemplatesTable
            data={productTemplateFulfillmentStatsData.productTemplates}
            loading={loading}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default Statistics;
